import React, { useState, useEffect, useRef } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

/**
 * BrandSelector Component
 * 
 * Dropdown selector for brands with search functionality
 * Works with brand_id (UUID) instead of brand name (string)
 */
const BrandSelector = ({ 
  value, // brand_id (UUID)
  onChange, // (brandId) => void
  error, 
  disabled = false,
  allowCreate = false, // Allow creating new brand on-the-fly
  className = ''
}) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Fetch all brands on mount
  useEffect(() => {
    fetchBrands();
  }, []);

  // Update selected brand when value changes
  useEffect(() => {
    if (value && brands.length > 0) {
      const brand = brands.find(b => b.id === value);
      setSelectedBrand(brand || null);
    } else {
      setSelectedBrand(null);
    }
  }, [value, brands]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/brands', {
        params: {
          is_active: true,
          limit: 100,
          sort: 'name',
          order: 'asc'
        }
      });
      setBrands(response.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      // Don't show error toast - brands might not be available yet
      // Just silently fail and show empty list
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    onChange(brand.id);
    setShowDropdown(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    setSelectedBrand(null);
    onChange(null);
    setSearchTerm('');
  };

  const handleToggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  // Filter brands based on search
  const filteredBrands = brands.filter(brand => {
    const search = searchTerm.toLowerCase();
    return (
      brand.name.toLowerCase().includes(search) ||
      brand.name_en?.toLowerCase().includes(search) ||
      brand.slug?.toLowerCase().includes(search)
    );
  });

  return (
    <div className={`relative ${className}`} dir="rtl" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        מותג
      </label>

      {/* Selected Brand Display */}
      <div
        onClick={handleToggleDropdown}
        className={`w-full px-3 py-2 border rounded-lg cursor-pointer transition-all ${
          error ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-primary-500'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${
          showDropdown ? 'ring-2 ring-primary-500 border-primary-500' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            {selectedBrand ? (
              <>
                {selectedBrand.logo && (
                  <img
                    src={selectedBrand.logo}
                    alt={selectedBrand.name}
                    className="w-6 h-6 object-contain"
                  />
                )}
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900">
                    {selectedBrand.name}
                  </span>
                  {selectedBrand.name_en && (
                    <span className="text-xs text-gray-500">
                      {selectedBrand.name_en}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <span className="text-gray-500">בחר מותג...</span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {selectedBrand && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 hover:bg-red-100 rounded-full transition-colors"
              >
                <XMarkIcon className="h-4 w-4 text-gray-500 hover:text-red-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-80 overflow-hidden">
          {/* Search */}
          <div className="p-3 border-b border-gray-200 sticky top-0 bg-white">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="חפש מותג..."
                className="w-full pr-9 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
          </div>

          {/* Brands List */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : filteredBrands.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <p className="text-sm">לא נמצאו מותגים</p>
                {allowCreate && searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      toast.info('יצירת ברנד חדש תתאפשר בקרוב');
                      setShowDropdown(false);
                    }}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <PlusIcon className="h-4 w-4 inline ml-1" />
                    צור מותג חדש "{searchTerm}"
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Clear Option */}
                <button
                  type="button"
                  onClick={handleClear}
                  className="w-full px-4 py-2 text-right hover:bg-red-50 transition-colors flex items-center gap-2 border-b border-gray-200"
                >
                  <XMarkIcon className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-600 font-medium">ללא מותג</span>
                </button>

                {/* Brands */}
                {filteredBrands.map((brand) => (
                  <button
                    key={brand.id}
                    type="button"
                    onClick={() => handleBrandSelect(brand)}
                    className={`w-full px-4 py-2.5 text-right hover:bg-primary-50 transition-colors flex items-center gap-3 ${
                      selectedBrand?.id === brand.id ? 'bg-primary-100' : ''
                    }`}
                  >
                    {/* Logo */}
                    {brand.logo ? (
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        className="w-8 h-8 object-contain flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-gray-500 font-bold">
                          {brand.name.charAt(0)}
                        </span>
                      </div>
                    )}

                    {/* Brand Info */}
                    <div className="flex-1 text-right">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {brand.name}
                        </span>
                        {brand.is_featured && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">
                            מומלץ
                          </span>
                        )}
                      </div>
                      {brand.name_en && (
                        <span className="text-xs text-gray-500">{brand.name_en}</span>
                      )}
                    </div>

                    {/* Selected Indicator */}
                    {selectedBrand?.id === brand.id && (
                      <CheckIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-600">
              {filteredBrands.length} מותגים זמינים
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandSelector;

